import type { Operation } from 'fast-json-patch'
import { CrashLinkStatus } from '@roadfootage/api-client'

export type CrashLinkDecision = 'keep' | 'unlink'

interface LinkedIncident {
  openDataCrashId?: string | null
  crashLinkStatus?: CrashLinkStatus | null
}

/**
 * JSON Patch operations for staff's decision on an incident's pending crash link (RF-070).
 *
 * - No link, or one already decided: nothing, so a plain edit never touches an approved or rejected link.
 * Each decision starts with a `test` on `/openDataCrashId`, so it only applies to the crash staff saw.
 *
 * - Unlink: `Rejected`, whether or not the report is being approved. The server keeps the id as an audit trail.
 * - Keep, while approving the report: `Approved`. Keeping without approving the report leaves the link pending.
 *
 * Append the result after the `/location` collapse in `buildPatch`: these ops never start with `/location`.
 */
export function crashLinkOps(
  incident: LinkedIncident | null | undefined,
  decision: CrashLinkDecision,
  approving: boolean
): Operation[] {
  if (!incident?.openDataCrashId) return []
  if (incident.crashLinkStatus !== CrashLinkStatus.Pending) return []

  // The decision is for the crash staff were shown. If the uploader re-linked since the dialog opened, the server refuses the
  // whole PATCH (409) instead of approving a crash nobody reviewed.
  const guard: Operation = { op: 'test', path: '/openDataCrashId', value: incident.openDataCrashId }

  if (decision === 'unlink') {
    return [guard, { op: 'replace', path: '/crashLinkStatus', value: CrashLinkStatus.Rejected }]
  }
  if (approving) {
    return [guard, { op: 'replace', path: '/crashLinkStatus', value: CrashLinkStatus.Approved }]
  }
  return []
}
